<script setup>
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStore } from 'vuex';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  description: { type: String, default: '' },
});

const emit = defineEmits(['toggle']);
const router = useRouter();
const store = useStore();

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const conversationStats = useMapGetter('conversationStats/getStats');
const unreadConversationCount = computed(() => {
  if (props.name !== 'Conversation') return 0;

  const unreadCounts = conversationStats.value?.aceleresBoxUnreadCounts || {};
  return ['novos', 'meus', 'outros', 'pessoais'].reduce(
    (total, box) => total + Number(unreadCounts[box] || 0),
    0
  );
});
const badgeValue = computed(() => {
  if (props.name === 'Conversation') return unreadConversationCount.value;

  return Number(dynamicCount.value || 0);
});
const count = computed(() =>
  badgeValue.value > 99 ? '99+' : badgeValue.value
);

onMounted(() => {
  if (props.name === 'Conversation') {
    store.dispatch('conversationStats/get', { immediate: true });
  }
});

const handleClick = event => {
  emit('toggle');

  if (
    !props.to ||
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  event.preventDefault();
  router.push(props.to).catch(() => {});
};
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-2 px-1.5 py-1 rounded-lg h-8 min-w-0 group"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'text-n-slate-12 bg-n-alpha-2 font-medium': isActive && !hasActiveChild,
      'text-n-slate-12 font-medium': hasActiveChild,
      'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
    }"
    @click.stop="handleClick"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div class="flex items-center gap-1.5 flex-grow min-w-0 flex-1">
      <span
        class="truncate"
        :class="{
          'text-body-main': !isActive,
          'font-medium text-sm': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="badgeValue"
        class="size-5 min-w-5 rounded-full bg-n-ruby-9 px-1 flex-shrink-0 grid place-items-center text-[10px] leading-none font-semibold text-n-ruby-3"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="description"
      class="i-lucide-info size-3 flex-shrink-0 opacity-0 group-hover:opacity-60 transition-opacity cursor-default"
      :title="description"
      @click.stop
    />
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
